import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { useInfiniteQuery } from 'react-query';
import { SuiContainer } from '@splashthat/sui';

import usePrevious from 'utils/hooks/usePrevious';
import CubeLoaderOverlay from 'common/components/CubeLoaderOverlay';
import CubeLoader, { INLINE_SIZE, MODAL_SIZE } from 'common/components/CubeLoader';
import Spacer from 'common/components/Spacer';
import NoResultsFound from 'common/components/NoResultsFound';

import UnsplashApiClient from 'lib/UnsplashApiClient';

import SearchBar from '../SearchBar';

import StockImage from './StockImage';
import StockNavBar from './StockNavBar';

import {
    Slide,
    StockSlideContentContainer,
    InfiniteScrollContainer,
    StockImageListContainer,
} from './StockSlide.styles';

const SEARCH_PLACEHOLDER = 'Search for the perfect image';
const TRY_LATER_MESSAGE = 'Service unavailable. Try later.';
const INPUT_DEBOUNCE_TIMEOUT = 750;
const BASE_CLASS_NAME = 'StockSlide';

export default function StockSlide({
    isActive,
    stockImageSearchValue,
    stockImages,
    stockImageSearchError,
    setStockImageSearchValue,
    setStockImages,
    setStockImageSearchError,
    setStockImageNavValue,
    stockImageNavValue,
    onImageSelected,
}) {
    const imageListViewWrapperRef = useRef(null);
    const unsplashApiClient = useRef(new UnsplashApiClient()).current;

    const previousIsActive = usePrevious(isActive);
    const previousStockImageSearchValue = usePrevious(stockImageSearchValue);
    const previousStockImageNavValue = usePrevious(stockImageNavValue);
    const searchTerm = stockImageSearchValue || stockImageNavValue;

    const { isLoading, isFetchingNextPage, hasNextPage, refetch, fetchNextPage } = useInfiniteQuery(
        ['stockImages', searchTerm],
        ({ pageParam = 1, queryKey }) => unsplashApiClient.searchPhotos(queryKey[1], pageParam),
        {
            select: (response) => ({
                images: response.pages.map(({ results }) => results).flat(Infinity),
            }),
            getNextPageParam: ({ total_pages: totalPages }, allPages) => {
                const numberOfPages = allPages.length;
                const hasMorePages = totalPages > numberOfPages;

                return hasMorePages ? numberOfPages + 1 : 1;
            },
            onSuccess: ({ images }) => {
                setStockImages(images);
                setStockImageSearchError('');
            },
            onError: () => {
                setStockImages([]);
                setStockImageSearchError(TRY_LATER_MESSAGE);
            },
            refetchOnWindowFocus: false,
        },
    );

    const isError = stockImageSearchError && !isLoading;
    const hasNoResults = !isLoading && !stockImageSearchError && !stockImages.length;

    const scrollImageListWrapperToTop = () => {
        const scrollContainer = imageListViewWrapperRef.current?.scrollContainer?.container;
        if (scrollContainer) {
            scrollContainer.scrollTop = 0;
        }
    };

    const updateNavValue = (navValue) => {
        setStockImageNavValue(navValue);
        navValue && setStockImageSearchValue('');
    };

    /**
     * Used for pagination. Triggered when image list scrolling gets close to bottom.
     */
    const onImageListScrolledToBottom = () => {
        if (!isLoading && hasNextPage) {
            fetchNextPage();
        }
    };

    const onStockImageSelected = (stockImage) => {
        // track in unsplashApiClient.
        unsplashApiClient.submitUnsplashAssetDownloaded(stockImage);

        onImageSelected(stockImage.urls.regular);
    };

    const searchFresh = () => refetch().then(scrollImageListWrapperToTop);
    const searchDebounced = useCallback(debounce(searchFresh, INPUT_DEBOUNCE_TIMEOUT), []);

    useEffect(() => {
        const slideJustBecameActive = !previousIsActive && isActive;
        const noPreviousResults = !stockImages.length && !stockImageSearchValue;

        if (slideJustBecameActive && noPreviousResults) {
            searchFresh();
        }
    }, [isActive, stockImages]);

    useEffect(() => {
        // If search input prop changed, debounce a search.
        if (previousStockImageSearchValue !== stockImageSearchValue) {
            searchDebounced();
        }
    }, [stockImageSearchValue]);

    useEffect(() => {
        // If nav changed, we search immediately
        if (stockImageNavValue && previousStockImageNavValue !== stockImageNavValue) {
            searchDebounced.cancel();
            searchFresh();
        }
    }, [stockImageNavValue]);

    useEffect(() => () => searchDebounced.cancel());

    return (
        <Slide className={BASE_CLASS_NAME} isActive={isActive}>
            <SuiContainer variation="flex-vertical full-height">
                {isActive && (
                    <SearchBar
                        placeholder={SEARCH_PLACEHOLDER}
                        setSearchValue={setStockImageSearchValue}
                        searchValue={stockImageSearchValue}
                        qaId={BASE_CLASS_NAME}
                    />
                )}
                <StockSlideContentContainer variation="flex full-height">
                    <StockNavBar
                        qaId={BASE_CLASS_NAME}
                        stockImageNavValue={stockImageNavValue}
                        updateNavValue={updateNavValue}
                    />
                    {hasNoResults ? (
                        <NoResultsFound qaId={BASE_CLASS_NAME} />
                    ) : (
                        <SuiContainer variation="flex flex-vertical full-height" grow={1}>
                            <InfiniteScrollContainer
                                onHitBottom={onImageListScrolledToBottom}
                                className={`${BASE_CLASS_NAME}__scroll-container ${BASE_CLASS_NAME}__scroll-container_gif-list`}
                                ref={imageListViewWrapperRef}
                                variation="flex-group flex-vertical"
                            >
                                {isError ? (
                                    <SuiContainer variation="flex full-height full-width">
                                        <p>{stockImageSearchError}</p>
                                    </SuiContainer>
                                ) : (
                                    <SuiContainer variation="flex-group flex-vertical full-height">
                                        <StockImageListContainer
                                            variation="flex full-height"
                                            className={`${BASE_CLASS_NAME}_stock-image-list`}
                                        >
                                            {(stockImages || []).map((stockImage) => (
                                                <StockImage
                                                    key={stockImage.id}
                                                    stockImage={stockImage}
                                                    BASE_CLASS_NAME={BASE_CLASS_NAME}
                                                    onStockImageSelected={onStockImageSelected}
                                                    href={unsplashApiClient.addUtmParamsToHref(
                                                        stockImage.user.links.html,
                                                    )}
                                                />
                                            ))}
                                        </StockImageListContainer>
                                        <Spacer />
                                    </SuiContainer>
                                )}
                            </InfiniteScrollContainer>
                            {isLoading && <CubeLoaderOverlay loading size={MODAL_SIZE} />}
                            {isFetchingNextPage && (
                                <SuiContainer variation="flex-group flex-center padded">
                                    <Spacer />
                                    <CubeLoader className={`${BASE_CLASS_NAME}_spinner`} size={INLINE_SIZE} />
                                </SuiContainer>
                            )}
                        </SuiContainer>
                    )}
                </StockSlideContentContainer>
            </SuiContainer>
        </Slide>
    );
}

StockSlide.propTypes = {
    isActive: PropTypes.bool,
    stockImageSearchValue: PropTypes.string,
    stockImages: PropTypes.array.isRequired,
    stockImageSearchError: PropTypes.string.isRequired,
    setStockImageSearchValue: PropTypes.func.isRequired,
    setStockImages: PropTypes.func.isRequired,
    setStockImageSearchError: PropTypes.func.isRequired,
    setStockImageNavValue: PropTypes.func.isRequired,
    stockImageNavValue: PropTypes.string,
    onImageSelected: PropTypes.func.isRequired,
};

StockSlide.defaultProps = {
    isActive: false,
    stockImageSearchValue: '',
    stockImageNavValue: null,
};
